const API_BASE_URL = (
  (typeof import.meta !== 'undefined' &&
    (import.meta as any).env?.VITE_API_URL) ||
  ''
).replace(/\/+$/, '');

export interface HourlyRetentionBucket {
  hour: number;
  hour_label: string;
  joined: number;
  left: number;
  net: number;
}

export interface RetentionSummary {
  total_joined: number;
  total_left: number;
  net_growth: number;
  churn_rate: number;
  total_ad_spend: number;
  real_cpl: number | null;
  current_total_members: number;
}

export interface CampaignAdSpend {
  id: string;
  tenant_id: string;
  campaign_name: string;
  amount: number;
  date: string;
  group_id?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CampaignAdSpendCreatePayload {
  campaign_name: string;
  amount: number;
  date?: string;
  group_id?: string;
  notes?: string;
}

export interface HourlyRetentionResponse {
  date: string;
  group_id?: string | null;
  summary: RetentionSummary;
  hourly_data: HourlyRetentionBucket[];
  campaigns: CampaignAdSpend[];
}

export async function fetchHourlyRetention(
  token: string,
  options?: {
    groupId?: string;
    date?: string;
    manualAdSpend?: number;
  }
): Promise<HourlyRetentionResponse> {
  const params = new URLSearchParams();
  if (options?.groupId) params.append('group_id', options.groupId);
  if (options?.date) params.append('date', options.date);
  if (options?.manualAdSpend !== undefined && options?.manualAdSpend !== null) {
    params.append('manual_ad_spend', options.manualAdSpend.toString());
  }

  const queryStr = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(
    `${API_BASE_URL}/api/v1/whatsapp-groups/telemetry/hourly-retention${queryStr}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg =
      typeof detail === 'string'
        ? detail
        : 'Erro ao consultar retenção horária.';
    throw new Error(msg);
  }

  return response.json();
}

export async function fetchCampaignAdSpends(
  token: string,
  options?: {
    date?: string;
    groupId?: string;
  }
): Promise<CampaignAdSpend[]> {
  const params = new URLSearchParams();
  if (options?.date) params.append('date', options.date);
  if (options?.groupId) params.append('group_id', options.groupId);

  const queryStr = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(
    `${API_BASE_URL}/api/v1/whatsapp-groups/ad-spend${queryStr}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg =
      typeof detail === 'string'
        ? detail
        : 'Erro ao consultar gastos de campanhas.';
    throw new Error(msg);
  }

  return response.json();
}

export async function createCampaignAdSpend(
  token: string,
  payload: CampaignAdSpendCreatePayload
): Promise<CampaignAdSpend> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/whatsapp-groups/ad-spend`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg =
      typeof detail === 'string'
        ? detail
        : 'Erro ao registrar investimento em tráfego.';
    throw new Error(msg);
  }

  return response.json();
}

export async function deleteCampaignAdSpend(
  token: string,
  id: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/whatsapp-groups/ad-spend/${id}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    const detail = errData?.detail;
    const msg =
      typeof detail === 'string'
        ? detail
        : 'Erro ao excluir gasto da campanha.';
    throw new Error(msg);
  }
}
